if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        //  Force blur the active input to trigger validation
        if (
          document.activeElement &&
          document.activeElement.tagName === "INPUT"
        ) {
          document.activeElement.blur();
        }

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        const quantityInputs = this.form.querySelectorAll("quantity-input");
        let allValid = true;
        let firstInvalidInput = null;

        quantityInputs.forEach((quantityInput) => {
          const valid = quantityInput.validateManualInput();
          if (!valid) {
            allValid = false;
            if (!firstInvalidInput) {
              firstInvalidInput = quantityInput.input;
            }
          }
        });

        if (!allValid) {
          firstInvalidInput.focus();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              localStorage.setItem(
                "cartAdjustmentMessage",
                "We adjusted your quantity to match order requirements."
              );

              window.location = window.routes.cart_url;
              return;
            }

            // Add succeeded — clear any stale MOQ/step validation message so the
            // cart drawer doesn't re-display it 200ms after re-render. The message
            // gets written to sessionStorage by showError() during a failed blur
            // validation; if the user then submits a corrected quantity, the
            // success path must wipe it before the drawer reads it.
            console.log('[FLUSH-MOQ] product-form success — clearing sessionStorage', {
              had: sessionStorage.getItem("cartAdjustmentMessage"),
              time: performance.now().toFixed(0),
            });
            sessionStorage.removeItem("cartAdjustmentMessage");

            // Also clear the error text rendered under the product page quantity
            // picker. showError() with addTimeout=true (MOQ branch) never
            // auto-clears, and validateManualInput's clearError() only runs when
            // a subsequent blur passes validation — which doesn't happen when the
            // user just clicks ATC after the auto-correction.
            // NOTE: this.form.id returns the <input name="id"> element (the
            // variant ID input), not the form's id string, due to the WHATWG
            // HTMLFormElement named-element access override. Use getAttribute
            // explicitly to get the id attribute as a string.
            const formIdString = this.form.getAttribute("id");
            const linkedInput = formIdString
              ? document.querySelector(`input[form="${formIdString}"]`)
              : null;
            const linkedQuantity = linkedInput?.closest("quantity-input");
            console.log('[FLUSH-MOQ] product-form looking for linked quantity-input', {
              formIdString,
              formIdProperty: typeof this.form.id === "string" ? this.form.id : "[OVERRIDDEN by named element]",
              foundInput: !!linkedInput,
              foundQuantityInput: !!linkedQuantity,
              hasClearError: typeof linkedQuantity?.clearError === "function",
              currentErrorText: linkedQuantity?.errorEl?.textContent,
            });
            if (linkedQuantity && typeof linkedQuantity.clearError === "function") {
              linkedQuantity.clearError();
              console.log('[FLUSH-MOQ] product-form called clearError on linkedQuantity', {
                resultErrorText: linkedQuantity.errorEl?.textContent,
              });
              // Sanity-check: log the error text 100ms and 1000ms later so we
              // can see if anything re-renders it after our clearError.
              setTimeout(() => {
                console.log('[FLUSH-MOQ] errorEl text 100ms after clearError', {
                  text: linkedQuantity.errorEl?.textContent,
                });
              }, 100);
              setTimeout(() => {
                console.log('[FLUSH-MOQ] errorEl text 1000ms after clearError', {
                  text: linkedQuantity.errorEl?.textContent,
                });
              }, 1000);
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
